/**
 * „Blender ohne Einzelfreigabe“ in der Karte „Vespera Desktop (Mac)“. (12.10.2026, Postfach Nr. 27; „Immer“ seit 15.10.2026)
 *
 *   Blender ohne Einzelfreigabe
 *   Immer an — Blender fragt nicht mehr, außer nach gelesenem Fremdtext.
 *   [ Aus ] [ Eine Stunde ] [ Immer (bis ich ausschalte) ]
 *
 * Drei Stellungen, eine davon gedrückt (aria-pressed). „Eine Stunde“ lässt Blender 60 Minuten ohne die Frage „Darf ich in Blender
 * arbeiten?“ ändern, Python ausführen, nachprüfen und speichern (erneut tippen = neue Stunde ab jetzt). „Immer“ bleibt an — auch über einen
 * Neustart —, bis Martin „Aus“ tippt (Martin, 15.10.: „… weil ich nicht nach 8 Stunden wieder den Scheiß einstellen muss!“). „Aus“ nimmt mit
 * EINEM Tipp alles zurück (Immer, Stunde, 4-Stunden-Ja). Rückgängig, die Sicherungen und die Sperre nach gelesenem Fremdtext bleiben
 * (shared/blender-freigabe.ts). Der Server entscheidet; die Seite zeigt nur den Stand (alle 15 s neu vom Server, dazwischen läuft die
 * Anzeige von selbst herunter). Die Karte ist unsichtbar, solange der Server sagt, dass dieses Konto den Schalter nicht hat.
 *
 * Barrierefreiheit: Satz in role=status/aria-live=polite, Knöpfe 44 px in einer benannten Gruppe, Zustand in Worten UND als aria-pressed.
 * Nur ein ECHTER Tipp zählt (isTrusted) — ein Skript, browser_click ohne echten Tipp oder ein Modell schaltet hier nichts.
 * Aussehen: style.css (`.blender-freigabe …`), keine Inline-Styles. (frontend/lib/karte.ts ist das Gerüst der Karten im VERLAUF; dieser Block
 * lebt in der Einstellungs-Karte und nutzt deren .mac-knopf.)
 */
import {
  BLENDER_FREIGABE_KOPF_WERT,
  BLENDER_FREIGABE_PFAD,
  NACH_FREMDTEXT_HINWEIS,
  standSatz,
  type BlenderFreigabeAktion,
  type BlenderFreigabeStand,
} from "../../shared/blender-freigabe.js";

export const TAKT_MS = 15_000;
const SATZ_FEHLER = "Das hat nicht geklappt. Bitte lade die Seite neu und versuche es noch einmal.";

type Stellung = "aus" | "stunde" | "dauer";

async function rufen(aktion?: BlenderFreigabeAktion): Promise<BlenderFreigabeStand | null> {
  try {
    const res = await fetch(BLENDER_FREIGABE_PFAD, {
      method: aktion ? "POST" : "GET",
      credentials: "same-origin",
      cache: "no-store",
      ...(aktion
        ? { headers: { "Content-Type": "application/json", "X-Vespera-Aktion": BLENDER_FREIGABE_KOPF_WERT }, body: JSON.stringify({ aktion }) }
        : {}),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as Partial<BlenderFreigabeStand>;
    if (typeof j.erlaubt !== "boolean" || typeof j.an !== "boolean" || typeof j.rest_s !== "number") return null;
    return {
      ok: true,
      erlaubt: j.erlaubt,
      an: j.an,
      rest_s: Math.max(0, Math.floor(j.rest_s)),
      dauer: j.dauer === true,
      ueber_neustart: j.ueber_neustart !== false,
    };
  } catch {
    return null;
  }
}

export function baueBlenderFreigabe(): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "blender-freigabe";
  wrap.hidden = true;

  const titel = document.createElement("h4");
  titel.className = "blender-freigabe-titel";
  titel.textContent = "Blender ohne Einzelfreigabe";
  const satz = document.createElement("p");
  satz.className = "blender-freigabe-satz";
  satz.setAttribute("role", "status");
  satz.setAttribute("aria-live", "polite");
  const hinweis = document.createElement("p");
  hinweis.className = "blender-freigabe-hinweis";
  hinweis.textContent = NACH_FREMDTEXT_HINWEIS;
  const wahl = document.createElement("div");
  wahl.className = "mac-geraet-aktionen blender-freigabe-wahl";
  wahl.setAttribute("role", "group");
  wahl.setAttribute("aria-label", "Blender ohne Einzelfreigabe: Aus, eine Stunde oder immer");

  const knopf = (text: string, stellung: Stellung, aktion: BlenderFreigabeAktion): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "mac-knopf";
    b.textContent = text;
    b.dataset.stellung = stellung;
    b.addEventListener("click", (ev) => {
      if (!ev.isTrusted) return;
      // „Aus“ und „Immer“ ein zweites Mal: nichts zu tun. „Eine Stunde“ erneut: neue Stunde ab jetzt.
      if (stellung !== "stunde" && aktuell() === stellung) return;
      void tippen(aktion);
    });
    return b;
  };
  const aus = knopf("Aus", "aus", "ausschalten");
  const stunde = knopf("Eine Stunde", "stunde", "einschalten");
  const immer = knopf("Immer (bis ich ausschalte)", "dauer", "dauer");
  wahl.append(aus, stunde, immer);
  wrap.append(titel, satz, wahl, hinweis);

  let stand: BlenderFreigabeStand | null = null;
  let bis = 0; // lokale Uhr: wann der Server-Rest endet
  let beschaeftigt = false;

  const rest = (): number => Math.max(0, Math.round((bis - Date.now()) / 1000));
  const aktuell = (): Stellung => (stand?.dauer ? "dauer" : stand?.an && rest() > 0 ? "stunde" : "aus");

  const zeichnen = (): void => {
    if (!stand?.erlaubt) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;
    const s = aktuell();
    satz.textContent = standSatz({ an: s !== "aus", rest_s: s === "stunde" ? rest() : 0, dauer: s === "dauer", ueber_neustart: stand.ueber_neustart });
    wrap.dataset.zustand = s === "aus" ? "aus" : s === "dauer" ? "dauer" : "an";
    for (const b of [aus, stunde, immer]) {
      b.setAttribute("aria-pressed", String(b.dataset.stellung === s));
      if (beschaeftigt) b.setAttribute("aria-disabled", "true");
      else b.removeAttribute("aria-disabled");
    }
  };
  const uebernehmen = (s: BlenderFreigabeStand | null): void => {
    if (!s) {
      satz.textContent = SATZ_FEHLER;
      wrap.dataset.zustand = "fehler";
      return;
    }
    stand = s;
    bis = Date.now() + s.rest_s * 1000;
    zeichnen();
  };
  const tippen = async (aktion: BlenderFreigabeAktion): Promise<void> => {
    if (beschaeftigt) return;
    beschaeftigt = true;
    zeichnen();
    const s = await rufen(aktion);
    beschaeftigt = false;
    uebernehmen(s);
    if (!s) wrap.hidden = false;
  };

  // Der Rest läuft von selbst herunter (jede Sekunde nur die Anzeige), der Server wird alle 15 s gefragt. Das Element hört auf, sobald es aus der Seite ist.
  let abgefragt = false;
  const takt = setInterval(() => {
    if (abgefragt && !wrap.isConnected) {
      clearInterval(takt);
      return;
    }
    if (!wrap.isConnected) return;
    zeichnen();
  }, 1000);
  const abfrage = setInterval(() => {
    if (!wrap.isConnected) {
      if (abgefragt) clearInterval(abfrage);
      return;
    }
    if (!beschaeftigt) void rufen().then((s) => s && uebernehmen(s));
  }, TAKT_MS);
  void rufen().then((s) => {
    abgefragt = true;
    if (s) uebernehmen(s);
  });
  return wrap;
}
